<template>
    <!-- Overlay du modal -->
    <div v-if="show" class="fixed inset-0 z-50 flex items-center justify-center  p-4 bg-black/50 backdrop-blur-sm"
        @click.self="emit('close')">
        <!-- Contenu du modal -->
        <div class="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden" @click.stop>
            <!-- Header avec alerte -->
            <div class="bg-red-50 border-b border-red-200 p-6">
                <div class="flex items-start gap-3">
                    <AlertTriangleIcon class="w-6 h-6 text-red-600 mt-0.5 flex-shrink-0" />
                    <div class="flex-1">
                        <h2 class="text-lg font-semibold text-red-900 mb-2">
                            Schéma manquant détecté
                        </h2>
                        <p class="text-sm text-red-700 mb-3">
                            Le MEMoS <strong>"{{ missingSchema.memoTitle }}"</strong> référence un schéma qui n'existe
                            plus :
                        </p>
                        <div class="bg-red-100 rounded-lg p-3 text-sm">
                            <div class="font-medium text-red-900">{{ missingSchema.originalName }}</div>
                            <div class="text-red-600">Type: {{ missingSchema.originalType }}</div>
                            <div class="text-red-500 text-xs">UUID: {{ missingSchema.originalUuid }}</div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Corps du modal -->
            <div class="p-6 overflow-y-auto max-h-[60vh]">
                <!-- Message d'explication -->
                <div class="mb-6">
                    <h3 class="text-base font-medium text-gray-900 mb-2 flex items-center gap-2">
                        <SearchIcon class="w-4 h-4" />
                        Schémas de remplacement disponibles
                    </h3>
                    <p class="text-sm text-gray-600">
                        Voici tous les schémas disponibles, triés par similarité de nom. Utilisez le filtre par type pour affiner votre recherche :
                    </p>
                </div>

                <!-- Sélecteur de type pour filtrer les schémas -->
                <div class="mb-4">
                    <label class="block text-sm font-medium text-gray-700 mb-2">Filtrer par type :</label>
                    <div class="flex flex-wrap gap-2">
                        <button 
                            class="px-3 py-1 text-sm rounded-full transition-colors"
                            :class="selectedFilterType === '' 
                                ? 'bg-blue-100 text-blue-800 border border-blue-300' 
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'"
                            @click="selectedFilterType = ''"
                        >
                            Tous les types
                        </button>
                        <button 
                            v-for="type in availableTypes" 
                            :key="type"
                            class="px-3 py-1 text-sm rounded-full transition-colors"
                            :class="selectedFilterType === type 
                                ? 'bg-blue-100 text-blue-800 border border-blue-300' 
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'"
                            @click="selectedFilterType = type"
                        >
                            {{ type }}
                        </button>
                    </div>
                </div>

                <!-- Liste des schémas suggérés -->
                <div v-if="filteredSchemas.length > 0" class="space-y-3 mb-6">
                    <div v-for="schema in filteredSchemas" :key="schema.uuid"
                        class="border rounded-lg p-4 cursor-pointer transition-all duration-200" :class="{
                            'border-blue-500 bg-blue-50': selectedReplacement === schema.uuid,
                            'border-gray-200 hover:border-gray-300 hover:bg-gray-50': selectedReplacement !== schema.uuid
                        }" @click="selectedReplacement = schema.uuid">
                        <div class="flex items-start justify-between">
                            <div class="flex items-start gap-3 flex-1">
                                <div class="mt-1">
                                    <input v-model="selectedReplacement" type="radio" :value="schema.uuid"
                                        class="w-4 h-4 text-blue-600" />
                                </div>
                                <div class="flex-1">
                                    <div class="flex items-center gap-2 mb-1">
                                        <h4 class="font-medium text-gray-900">{{ schema.appellation }}</h4>
                                        <CheckIcon v-if="schema.similarity >= 80" class="w-4 h-4 text-green-600" />
                                    </div>
                                    <p class="text-sm text-gray-600">{{ schema.description || '' }}</p>
                                </div>
                            </div>
                            <div class="ml-3 text-right">
                                <div class="text-xs bg-gray-100 px-2 py-1 rounded mb-1">
                                    {{ schema.similarity }}% similaire
                                </div>
                                <div class="text-xs text-gray-500">
                                    {{ schema.type }}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Aucun schéma trouvé -->
                <div v-else class="text-center py-8 text-gray-500">
                    <SearchIcon class="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p class="text-sm">
                        {{ selectedFilterType 
                            ? `Aucun schéma de type "${selectedFilterType}" trouvé` 
                            : 'Aucun schéma disponible' 
                        }}
                    </p>
                </div>

                <!-- Warning sur les data_entries -->
                <div class="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                    <div class="flex items-start gap-2">
                        <InfoIcon class="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                        <div class="text-sm">
                            <p class="font-medium text-amber-800 mb-1">Conservation des données</p>
                            <p class="text-amber-700">
                                Toutes les données collectées seront conservées. Si certaines ne correspondent plus
                                au nouveau schéma, elles seront marquées comme incompatibles mais resteront visibles
                                et modifiables.
                            </p>
                        </div>
                    </div>
                </div>

                <!-- Actions -->
                <div class="flex gap-3 justify-end">
                    <button class="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors" @click="handleCancel">
                        Annuler
                    </button>
                    <button :disabled="!selectedReplacement || filteredSchemas.length === 0"
                        class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        @click="handleApplyReplacement">
                        Appliquer le remplacement
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import type { SchemaWithSectionList } from '@/stores/schemasStore';
import { AlertTriangleIcon, CheckIcon, InfoIcon, SearchIcon } from 'lucide-vue-next';
import { computed, ref, watch } from 'vue';

// Types
interface MissingSchemaInfo {
    originalUuid: string;
    originalType: string;
    originalName: string;
    memoTitle: string;
}

interface SuggestedSchema extends SchemaWithSectionList {
    similarity: number;
}

// Props
interface Props {
    show: boolean;
    missingSchema: MissingSchemaInfo;
    availableSchemas: SchemaWithSectionList[];
}

const props = withDefaults(defineProps<Props>(), {
    show: false,
    availableSchemas: () => []
});

// Emits
interface Emits {
    (e: 'close'): void;
    (e: 'schema-replaced', newSchema: SchemaWithSectionList): void;
    (e: 'cancel'): void;
}

const emit = defineEmits<Emits>();

// État local
const selectedReplacement = ref<string>('');
const selectedFilterType = ref<string>('');

// Algorithme de distance de Levenshtein
const levenshteinDistance = (str1: string, str2: string): number => {
    const matrix = Array(str2.length + 1).fill(null).map(() => Array(str1.length + 1).fill(null));

    for (let i = 0; i <= str1.length; i++) matrix[0][i] = i;
    for (let j = 0; j <= str2.length; j++) matrix[j][0] = j;

    for (let j = 1; j <= str2.length; j++) {
        for (let i = 1; i <= str1.length; i++) {
            const indicator = str1[i - 1] === str2[j - 1] ? 0 : 1;
            matrix[j][i] = Math.min(
                matrix[j][i - 1] + 1,     // deletion
                matrix[j - 1][i] + 1,     // insertion
                matrix[j - 1][i - 1] + indicator // substitution
            );
        }
    }

    return matrix[str2.length][str1.length];
};

// Calcul de similarité
const calculateSimilarity = (original: string, candidate: string): number => {
    if (!original || !candidate) return 0;

    const normalizedOriginal = original.toLowerCase().trim();
    const normalizedCandidate = candidate.toLowerCase().trim();

    if (normalizedOriginal === normalizedCandidate) return 100;

    const distance = levenshteinDistance(normalizedOriginal, normalizedCandidate);
    const maxLength = Math.max(normalizedOriginal.length, normalizedCandidate.length);

    if (maxLength === 0) return 100;

    return Math.round(((maxLength - distance) / maxLength) * 100);
};

// Types disponibles
const availableTypes = computed((): string[] => {
    const types = new Set(props.availableSchemas.map(schema => schema.type));
    return Array.from(types).sort();
});

// Schémas suggérés avec similarité pour tous les schémas
const allSchemasWithSimilarity = computed((): SuggestedSchema[] => {
    if (!props.availableSchemas.length) {
        return [];
    }

    return props.availableSchemas
        .map(schema => ({
            ...schema,
            similarity: calculateSimilarity(props.missingSchema.originalName, schema.appellation)
        }))
        .sort((a, b) => b.similarity - a.similarity); // Tri par similarité décroissante
});



// Schémas filtrés selon le type sélectionné
const filteredSchemas = computed((): SuggestedSchema[] => {
    if (selectedFilterType.value === '') {
        // Si aucun filtre, proposer tous les schémas avec similarité
        return allSchemasWithSimilarity.value.slice(0, 20); // Limiter à 20 pour éviter la surcharge
    }
    
    return allSchemasWithSimilarity.value
        .filter(schema => schema.type === selectedFilterType.value)
        .slice(0, 20); // Limiter à 20 par type
});

// Gestionnaires d'événements
const handleCancel = (): void => {
    selectedReplacement.value = '';
    emit('cancel');
    emit('close');
};

const handleApplyReplacement = (): void => {
    if (!selectedReplacement.value) return;

    const selectedSchema = filteredSchemas.value.find(s => s.uuid === selectedReplacement.value);
    if (selectedSchema) {
        emit('schema-replaced', selectedSchema);
        emit('close');
    }
};

// Auto-sélection du schéma le plus similaire si > 80%
const autoSelectBestMatch = (): void => {
    if (filteredSchemas.value.length > 0 && filteredSchemas.value[0].similarity >= 80) {
        selectedReplacement.value = filteredSchemas.value[0].uuid;
    }
};

// Watcher pour auto-sélection
watch(() => filteredSchemas.value, () => {
    if (props.show) {
        autoSelectBestMatch();
    }
}, { immediate: true });

// Initialiser le filtre par défaut
watch(() => props.show, (newShow) => {
    if (newShow) {
        // Si on a un type original, essayer de le sélectionner par défaut
        if (props.missingSchema.originalType && props.missingSchema.originalType !== 'Inconnu') {
            selectedFilterType.value = props.missingSchema.originalType;
        } else {
            selectedFilterType.value = ''; // Tous les types
        }
    }
}, { immediate: true });

// Reset sélection quand le modal se ferme
watch(() => props.show, (newShow) => {
    if (!newShow) {
        selectedReplacement.value = '';
    }
});
</script>

<style scoped>
/* Animation d'apparition du modal */
.modal-enter-active,
.modal-leave-active {
    transition: all 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
    opacity: 0;
    transform: scale(0.9);
}

/* Scrollbar personnalisée pour la liste des schémas */
.overflow-y-auto::-webkit-scrollbar {
    width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
}
</style>